import type {
  Sound,
  MistakeText,
  MistakeVideo,
  MusicType,
  DTOEntity,
  ExerciseDetails,
  LegalPage,
  LanguageKey,
  TreatmentTemplateDetails,
} from "@akina/domain";
import type { ReleaseNotes } from "@akina/infra";
import slugify from "slugify";

type CMSLinkField = {
  url: string;
  cached_url: string;
};
export type CMSMediaData<T extends string = string> = {
  enum: T;
  media_url: CMSLinkField;
  content: string;
};

export type CMSVerbalAudioPool = CMSStory<{ Pool: CMSMediaData[] }>;
export type CMSStory<T> = {
  slug: string;
  full_slug: string;
  content: T;
} & DTOEntity;

export type CMSMultiStoryData<T> = {
  stories: CMSStory<T>[];
};

export type CMSMultiStoryResult<T> = {
  data: CMSMultiStoryData<T>;
};

export type CMSAsset = {
  filename: string;
};

export type CMSVideoAndAudioData = {
  Audio: CMSMediaData[];
  Video: CMSMediaData[];
  _uid: string;
  component: string;
};

export type CmsStoryContent = {
  slug?: string;
  Text?: string;
  Title?: string;
  DisplayedName?: string;
  SubText?:
    | {
        content?: { content?: { text?: string }[] }[];
      }
    | string;
  BoxTitle?: string;
  ContentExample?: string;
  MainText?: string;
  TooltipText?: string;
  singular?: string;
  plural?: string;
  Key?: string;
  Description?: string;
  title?: string;
  subtitle?: string;
  version?: string;
  date?: string;
  content?: string;
  preContent?: string;
  PDF?: {
    filename: string;
  };
  name?: string;
  key?: string;
  phases?: {
    key?: string;
    goal?: string;
    name?: string;
  }[];
  questions?: {
    question?: string;
    answer?: string;
  }[];
  BodyPart?: string;
  Thumbnail?: CMSAsset;
  PreviewGif?: CMSAsset;
  MediaURL?: CMSAsset;
  InstructionTextBullets?: { Text: string }[];
  InstructionVideoAndAudio?: CMSVideoAndAudioData[];
  MistakeVideos?: MistakeVideo[];
  PageTitle?: string;
  PageSubtitle?: string;
  TableOfContentsTitle?: string;
  Releases?: {
    ReleaseKey: string;
    ReleaseTitle: string;
    ReleaseDate: string;
    Content: string;
  }[];
  Name?: string;
  Goal?: string;
  Image?: CMSAsset;
  HeroImage?: CMSAsset;
  URLLink?: { url: string };
  MusicType?: MusicType;
  IsDisabled?: boolean;
  vc_pool?: {
    component: string;
    key: VcPoolType;
    ve_pool: VePool[];
  }[];
  Maintenances?: {
    Tag: string;
    Title: string;
    Content: string;
    ReleaseDate: string;
    ReleaseEnd: string;
    Deployed: boolean;
  }[];
};

export enum VcPoolType {
  neutral_pose = "neutral_pose",
  mistake = "mistake",
  wait = "wait",
  instruction = "instruction",
  perspective = "perspective",
}

export type VePool = {
  key: string;
  component: string;
  duration: number;
  media_url: { url: string; cached_url?: string };
};

export enum VoicePoolType {
  default_pool = "default_pool",
  energized_pool = "energized_pool",
  laidback_pool = "laidback_pool",
}

export type CmsCopy = {
  localeCopy: Record<string, string>;
  otherStories: Record<string, CmsStoryContent>;
};
export type NullableCmsCopy = CmsCopy | null | undefined;

export type CmsCopyStoreCare = {
  cmsCopy: NullableCmsCopy;
  actions: CmsCopyStoreActionsCare;
};

export type CmsCopyStoreSession = {
  cmsCopy: NullableCmsCopy;
  actions: CmsCopyStoreActionsSession;
};

export type CmsCopyStorePortal = {
  cmsCopy: NullableCmsCopy;
  actions: CmsCopyStoreActionsPortal;
};

export type CmsCopyStore = {
  cmsCopy: NullableCmsCopy;
  isLoadingCmsCopy: boolean;
  actions: CmsCopyStoreActionsCare & CmsCopyStoreActionsSession & CmsCopyStoreActionsPortal;
};

type CmsCopyStoreActionsCare = {
  setCmsCopy: (newCopy: NullableCmsCopy) => void;
  setIsLoadingCmsCopy: (value: boolean) => void;
  getLegalPage: (key: LegalPage["key"], app: "care" | "portal", language?: LanguageKey | null) => LegalPage | undefined;
  getIFULanguages: () => { key: LanguageKey; name: string }[];
  getReleaseNotes: (app: "care" | "portal") => ReleaseNotes | undefined;
  getSounds: (type: Sound["type"]) => Sound[];
  getTreatmentTemplateDetails: (key: string | undefined) => TreatmentTemplateDetails | undefined;
  getExerciseDetails: (key: string) => ExerciseDetails | undefined;
  getImageByKey: (key: string) => string | undefined;
};

type CmsCopyStoreActionsSession = {
  setCmsCopy: (newCopy: CmsCopy) => void;
  getSoundByKey: (type: Sound["type"], key: string) => Sound | undefined;
  getSounds: (type: Sound["type"]) => Sound[];
  getExerciseDetails: (key: string) => ExerciseDetails | undefined;
};

type CmsCopyStoreActionsPortal = {
  setCmsCopy: (newCopy: CmsCopy) => void;
  getExerciseMistakesText: () => MistakeText[];
  getLegalPage: (key: LegalPage["key"], app: "care" | "portal", language?: LanguageKey | null) => LegalPage | undefined;
  getIFULanguages: () => { key: LanguageKey; name: string }[];
  getTreatmentTemplatesDetails: () => TreatmentTemplateDetails[];
  getTreatmentTemplateDetails: (key: string | undefined) => TreatmentTemplateDetails | undefined;
  getExerciseDetails: (key: string) => ExerciseDetails | undefined;
  getAllExerciseNames: () => { key: string; name: string }[];
  getExerciseMistakeVideos: (exercise: string) => { key: string; url: string }[];
  getReleaseNotes: (app: "care" | "portal") => ReleaseNotes | undefined;
};

export const slugifyCmsKey = (key: string) =>
  key
    .replace(/_/g, "-")
    .split("/")
    .map(s => slugify(s, { replacement: "-", lower: true, trim: true }))
    .join("/");

export const CMS_PATHS = {
  MISTAKE_FEEDBACK_TEMPLATES: "shared/session-v2/mistake-templates",
  EXERCISE: (slug: string) => `shared/session-v2/exercise-templates/${slug}`,
  EXERCISE_NAMES: "shared/session-v2/exercise-templates",
  TREATMENT_TEMPLATES: "shared/treatment-templates",
  TREATMENT_TEMPLATE: (slug: string) => `shared/treatment-templates/${slug}`,

  IFU_LANGUAGES: "shared/ifu-language-options",
  SOUND: (slug: Sound["type"]) => `care/sounds/${slug}`,
  IMAGE: (slug: string) => `care/images/${slug}`,
  RELEASE_NOTES: {
    care: "shared/release-notes/care",
    portal: "shared/release-notes/portal",
  },
  LEGAL_PAGE: {
    care: "care/profile-and-settings/legal-pages/",
    portal: "portal/settings/legal-pages/",
  },
};

type CmsGetter<T, Params extends unknown[] = []> = (...params: Params) => T;

export const getCmsContent = <T, Params extends unknown[] = []>(
  cmsGetter: CmsGetter<T, Params>,
  ...params: Params
): Promise<T> => {
  return Promise.resolve(cmsGetter(...params));
};

export type CmsSetType = (fn: (state: { cmsCopy: NullableCmsCopy }) => void) => void;
export type CmsGetType = () => { cmsCopy: NullableCmsCopy };
